import { faqJsonLd } from "@amtana/browser";
import type { defineBrowserModule, SeoDeclaration } from "@amtana/browser";
import { shippedLanguage } from "@amtana/i18n/catalog";
import type { Phrase } from "@amtana/i18n/catalog";

import { familyBreadcrumb } from "./family-head.web.ts";
import type { ComparisonPage, ComparisonWords } from "./features/comparisons/model/comparison-page.ts";
import { pageOfComparison } from "./features/comparisons/model/comparison-pages.ts";
import { COMPARISONS_WORDS_EN } from "./features/comparisons/model/comparisons-words-en.ts";
import { COMPARISONS_WORDS_ES } from "./features/comparisons/model/comparisons-words-es.ts";

const FAMILY = "modules/site/browser/src/features/comparisons";

function pageAt(path: string): ComparisonPage {
  const page = pageOfComparison(path);
  if (!page) throw new Error(`No comparison page at "${path}": add it to COMPARISON_PAGES.`);
  return page;
}

function phraseOf(path: string, read: (words: ComparisonWords) => string): Phrase {
  const page = pageAt(path);
  return { kind: "phrase", read: (language) => read(page.words[shippedLanguage(language)]) };
}

/** A comparison page's title, in the active language. */
function titleOf(path: string): Phrase {
  return phraseOf(path, (words) => words.title);
}

/** A comparison page's search head: its search, its breadcrumb and the FAQ its body shows (ADR-034
 * points 6 and 7). */
function seoOf(path: string): SeoDeclaration {
  const page = pageAt(path);
  return {
    description: phraseOf(path, (words) => words.description),
    keyphrase: phraseOf(path, (words) => words.keyphrase),
    contentType: "landing",
    jsonLd: (language) => {
      const shipped = shippedLanguage(language);
      const words = page.words[shipped];
      return [
        familyBreadcrumb(language, [
          { name: shipped === "es" ? COMPARISONS_WORDS_ES.home : COMPARISONS_WORDS_EN.home, path: "/" },
          { name: words.linkLabel, path: page.path },
        ]),
        faqJsonLd(words.questions),
      ];
    },
    sources: [
      `${FAMILY}/ui/blocks/comparison-block.tsx`,
      `${FAMILY}/model/${page.slug}-words-en.ts`,
      `${FAMILY}/model/${page.slug}-words-es.ts`,
      `${FAMILY}/model/comparison-sources.ts`,
    ],
  };
}

/** The comparisons family's screens (ADR-034 point 1). Keep a literal `.withScreens({ ... })`, never a
 * spread: the `screen-seo` policy reads this file as text. */
export function withComparisonScreens(site: ReturnType<typeof defineBrowserModule>) {
  return site.withScreens({
    comparisonInstagramEmailExtractors: {
      path: "/compare/instagram-email-extractors",
      title: titleOf("/compare/instagram-email-extractors"),
      seo: seoOf("/compare/instagram-email-extractors"),
      load: () => import("./features/comparisons/ui/sections/comparison-screen.tsx"),
    },
  });
}
